<template>
    <section :id="sectionId" class="mb-10">
        <h2 class="text-xl font-semibold mb-2">ReferOn</h2>
        <Accordion :multiple="true" v-model:activeIndex="accordionActive">
            <AccordionTab header="Setting up tracking links">
                <div class="text-sm leading-relaxed">
                    <p class="mb-3">
                        ReferOn is the simplest flow: <strong>tracking links come from the operator only</strong> — there
                        is no admin panel we log into. Once the operator sends you the link, paste it directly into the
                        pending SiteBrandLink in AffiliationSpace. On paste, the app auto-fills the
                        <strong>Internal Note</strong> and rewrites the URL with the postback parameters we need.
                    </p>

                    <ol class="list-decimal ml-6 mb-4 space-y-1">
                        <li>Ask the operator for the tracking URL for this publisher / brand. It comes in the shape<br />
                            <code class="bg-black/40 px-1 py-0.5 rounded">https://sgc.monalvor.com/?mid=251962_2116744</code>
                            — the <code>mid</code> is <em>&lt;campaignId&gt;_&lt;publisherId&gt;</em>.</li>
                        <li>Go to <strong>Advertisers</strong> in the sidebar, expand the brand, and find the pending row.</li>
                        <li>Paste the URL into <strong>Affiliate Link</strong>. That's it — do not touch Internal Note.</li>
                        <li>The enricher fills Internal Note with the suffix after the underscore
                            (<code>2116744</code> above) and appends <code>&amp;pubid=2116744&amp;clickid={clickid}</code>
                            so postbacks resolve.</li>
                        <li>Save — the row's Link status flips to <span class="text-green-400 font-semibold">linked</span>.</li>
                    </ol>

                    <p class="mb-2 font-medium">Where you paste it</p>
                    <div class="mb-4">
                        <img :src="pasteTargetScreenshot"
                             alt="AffiliationSpace — pending SiteBrandLink row with empty Internal Note + Affiliate Link"
                             class="rounded border border-gray-700"
                             style="width: 100%; max-width: 1400px; height: auto;" />
                        <p class="text-xs text-gray-400 mt-1">
                            Paste the operator URL into <strong>Affiliate Link</strong>. Internal Note fills itself.
                        </p>
                    </div>

                    <p class="mb-1 font-medium">What the enricher does</p>
                    <div class="mb-4 p-3 rounded bg-black/40 text-xs font-mono">
                        <p class="text-gray-400 mb-1">// Raw (from operator)</p>
                        <p class="mb-2">https://sgc.monalvor.com/?mid=251962_2116744</p>
                        <p class="text-gray-400 mb-1">// Enriched (what actually gets saved)</p>
                        <p class="mb-2">https://sgc.monalvor.com/?mid=251962_2116744&amp;pubid=<span class="text-green-400">2116744</span>&amp;clickid=<span class="text-green-400">{clickid}</span></p>
                        <p class="text-gray-400 mb-1">// Internal Note</p>
                        <p><span class="text-green-400">2116744</span></p>
                    </div>

                    <div class="p-3 rounded border border-amber-400/50 bg-amber-50/10 text-xs mb-4">
                        <p class="font-semibold mb-1">Internal Note must stay unique per SpaceAdvertiserLogin</p>
                        <p>
                            The autofill uses the publisher ID from the <code>mid</code>, so collisions only happen if the
                            operator hands the same <code>mid</code> to two publishers under the same partner login. If you
                            hit the &quot;duplicate internalNote&quot; save error, go back to the operator and get a link
                            with a different <code>mid</code> suffix rather than editing the Internal Note by hand.
                        </p>
                    </div>

                    <div class="p-3 rounded border border-blue-400/50 bg-blue-950/30 text-xs">
                        <p class="font-semibold mb-1">Format validation</p>
                        <p>
                            The enricher (<code>src/utils/affiliate-link-enricher.js</code>, <code>referon</code> handler)
                            requires a <code>mid</code> query param containing an underscore. If the URL doesn't match,
                            Internal Note stays empty and nothing is appended — fix the URL with the operator before saving.
                        </p>
                    </div>
                </div>
            </AccordionTab>
        </Accordion>
    </section>
</template>

<script setup>
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import { useHelpSection } from './useHelpSection';
import pasteTargetScreenshot from '@/assets/docs/referon-paste-target.png';
const { sectionId, accordionActive } = useHelpSection('links-referon');
</script>
